import * as React from "react";
import { Grid, ListItem, ListItemText, makeStyles, StyleRules, Typography } from "@material-ui/core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { IconName } from "@fortawesome/pro-light-svg-icons";
import { PentairBlue, PentairSilverGray, PentairTheme } from "@pentair/react";
import { IMicroAppFeature } from "../../common/types";
import { onNavigate } from "../../../config/routes";
import { buildLogicLayerNavPath } from "../../../config/constants";
import { useFeatureAccessContext } from "../../../context/FeatureAccessContext";

export interface INavNode {
  id: string;
  name: string;
  path: string;
  type: "folder" | "page";
  icon: string;
  order: number;
  isVisible: boolean;
  isVisibleInMenu: boolean;
  description?: string;
  children?: INavNode[];
}

export interface INavigationConfig {
  id: string;
  name: string;
  path: string;
  type: string;
  children: INavNode[];
  [key: string]: any;
}

const useStyles = makeStyles((theme: PentairTheme): StyleRules => ({
  title: { color: PentairSilverGray[500] },
  icon: {
    color: PentairBlue[500],
    marginRight: theme.spacing(1),
  },
  listItem: {
    "&:hover": {
      backgroundColor: "transparent !important",
      color: theme.palette.brightBlue.main,
      borderBottomColor: theme.palette.brightBlue.main,
    },
  },
  listItemPrimary: { fontWeight: 600 },
  listItemSecondary: { fontWeight: 400, fontSize: "14px" },
}));

interface INavigationMenuSectionProps {
  navigationConfig: INavigationConfig;
  microAppFeature: IMicroAppFeature;
}

export const NavigationMenuSection: React.FC<INavigationMenuSectionProps> = ({ navigationConfig, microAppFeature }) => {
  const classes = useStyles();
  const featureAccess = useFeatureAccessContext();
  const rootChildren = (navigationConfig.children || []) as INavNode[];

  const visibleGroups = rootChildren
    .filter((node) => {
      // If permissions are still loading, show the node
      if (!featureAccess) {
        return node.type === "folder" && node.isVisible;
      }
      // Once permissions loaded, apply access check
      return node.type === "folder" && node.isVisible && featureAccess.canAccess(node.name);
    })
    .sort((a, b) => a.order - b.order);

  const handleNavigate = (pageId: string) => {
    onNavigate(
      microAppFeature.microapp,
      `${buildLogicLayerNavPath(navigationConfig.id, pageId)}`,
      microAppFeature.domain,
    );
  };

  return (
    <>
      {visibleGroups.map((group) => {
        // Level 2: visible-in-menu pages
        const visiblePages = (group.children || [])
          .filter((child) => child.isVisibleInMenu)
          .sort((a, b) => a.order - b.order);

        if (visiblePages.length === 0) return null;

        // Parse icon — supports "fal fa-cube" format
        const iconParts = group.icon.split(" ");
        const iconName = (iconParts.length > 1 ? iconParts[1].replace("fa-", "") : iconParts[0].replace("fa-", "")) as IconName;

        return (
          <Grid item xs={12} sm={6} md={5} lg={4} key={group.id}>
            <div style={{ display: "flex" }}>
              <FontAwesomeIcon
                icon={["fal", iconName]}
                size="lg"
                className={classes.icon}
              />
              <Typography paragraph variant="h4" className={classes.title}>
                {group.name}
              </Typography>
            </div>
            {visiblePages.map((page) => (
              <ListItem
                button
                divider
                key={page.id}
                disableGutters
                onClick={() => handleNavigate(page.path)}
                className={classes.listItem}
              >
                <ListItemText
                  classes={{ primary: classes.listItemPrimary }}
                  primary={page.name}
                  secondary={
                    page.description ? (
                      <Typography className={classes.listItemSecondary}>
                        {page.description}
                      </Typography>
                    ) : undefined
                  }
                />
              </ListItem>
            ))}
          </Grid>
        );
      })}
    </>
  );
};
